import {
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
  GridValidRowModel,
} from "@mui/x-data-grid";
import { useGetLocale } from "@refinedev/core";
import { useDataGrid } from "@refinedev/mui";
import { trTR, enUS } from "@mui/x-data-grid/locales";
import { ProductListFilterRow } from "./ProductListFilterRow";
import { useEffect, useMemo, useState } from "react";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { ProductSelectionRow } from "./ProductSelectionRow";
import { SearchIndicators } from "../../../../components/dataGridFilter/SearchIndicators";
import { useStore } from "zustand";
import { getQueryFromFilter } from "../../../../components/dataGridFilter/getQueryFromFilter";
import { getInventoryFilterStore } from "./DetailedSearch/utils/getInventoryFilterStore";
import { FilterProvider } from "../../../../components/dataGridFilter/filter-context/FilterProvider";
import { SyncrosaleLoader } from "../../../../components/layout/components/SyncrosaleLoader";

export type StoreProductStatus =
  | "IN_CRITERIA_QUEUE"
  | "OUT_OF_CRITERIA"
  | "PENDING_APPROVE"
  | "IN_LISTING_QUEUE"
  | "DELETED"
  | "ACTIVE"
  | "ACCEPTED"
  | "INACTIVE"
  | "NO_DATA"
  | "INCOMPLETE";

export type MarketplaceProductStatus = "QUEUED" | "ACTIVE" | "NOT_FOUND";

export type AmazonListingItemStatus = "DISCOVERABLE" | "BUYABLE";

type ProductListProps = {
  columns: GridColDef<GridValidRowModel>[];
  storeProductStatus?: StoreProductStatus;
  marketplaceProductStatus?: MarketplaceProductStatus;
  amazonListingItemStatus?: AmazonListingItemStatus;
  rowHeight?: number;
  hasRowSelection?: boolean;
};

export const ProductList = <T extends GridValidRowModel>({
  columns,
  storeProductStatus,
  marketplaceProductStatus,
  amazonListingItemStatus,
  hasRowSelection,
}: ProductListProps) => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const locale = useGetLocale();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  useEffect(() => {
    setRowSelectionModel([]);
  }, [hasRowSelection]);

  const filterStore = getInventoryFilterStore(
    storeProductStatus,
    marketplaceProductStatus,
    amazonListingItemStatus,
  );

  const filterStatus = useStore(filterStore);

  const predicateType = "and";

  const query = useMemo(
    () => getQueryFromFilter(filterStatus.filters, predicateType),
    [filterStatus.filters, predicateType],
  );

  const {
    dataGridProps,
    sorters,
    tableQuery: { isInitialLoading },
  } = useDataGrid<T>({
    resource: `store/${selectedCountry?.id}/product/detailed2`,
    queryOptions: { enabled: !!selectedCountry },
    sorters: { initial: [{ field: "created", order: "desc" }] },
    meta: {
      queries: query,
    },
  });

  if (isInitialLoading) return <SyncrosaleLoader />;

  return (
    <FilterProvider filterStore={filterStore}>
      {rowSelectionModel.length > 0 ? (
        <ProductSelectionRow
          resetSelection={() => setRowSelectionModel([])}
          selectedItems={rowSelectionModel}
        />
      ) : (
        <ProductListFilterRow
          sorters={sorters}
          marketplaceProductStatus={marketplaceProductStatus ?? ""}
          storeProductStatus={storeProductStatus ?? ""}
        />
      )}
      <SearchIndicators />
      <DataGrid
        {...dataGridProps}
        sx={{
          flex: 1,
          "& .MuiDataGrid-row": {
            cursor: hasRowSelection ? "default" : "pointer",
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: hasRowSelection
              ? undefined
              : "transparent !important",
          },
          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: hasRowSelection
              ? undefined
              : "transparent !important",
          },
        }}
        rowHeight={96}
        columns={columns}
        localeText={
          (locale() === "tr" ? trTR : enUS).components.MuiDataGrid.defaultProps
            .localeText
        }
        disableColumnFilter
        checkboxSelection={hasRowSelection}
        getRowId={(row) => row.asin}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        keepNonExistentRowsSelected
        hideFooterSelectedRowCount
      />
    </FilterProvider>
  );
};
